import './active-operate.less';
import classnames from 'classnames';
import React, { useState } from 'react';
import { getOperateDirection, getOperateType, MoveListenerLevel, useMoveEventListener, useScale } from '@/framework';
import { useResizeCursor } from '@/utils/cursor-kit';
import { ITouchKitEvent } from '@/utils/event-kit';
import { getPointsBetweenAngle } from '@/utils/math-kit';
import {
    BaseLayerViewType,
    ILayerProps,
    IMarkupLayerProps,
    LayerType,
    MarkupType,
    ResizeOperateType,
    ScaleOperateType,
} from '@/version-types';
import { useArrowOperate } from '../hooks/use-arrow-operate';
import { useSegmentOperate } from '../hooks/use-segment-operate';

const getOperateOpacity = (
    operateType: ResizeOperateType | ScaleOperateType | null,
    type: ScaleOperateType | ResizeOperateType,
) => {
    const opacity = operateType ? (operateType === type ? 1 : 0) : 1;
    const res: any = { opacity };
    if (operateType) {
        res.pointerEvents = operateType === type ? 'all' : 'none';
        res.padding = operateType === type ? 10 : 0;
    } else {
        res.pointerEvents = 'all';
        res.padding = 0;
    }
    return res;
};

const ArrowOperate: React.FC<{ layer: ILayerProps }> = React.memo(({ layer }) => {
    const [curOperateType, setCurOperateType] = useState<ResizeOperateType | null>(null);
    const arrowOperate = useArrowOperate();
    const scale = useScale();
    const hasLine = (layer?.data as IMarkupLayerProps).hasLine;

    useMoveEventListener(
        (e: ITouchKitEvent) => {
            const operateType = getOperateType(e.target);
            const direction = getOperateDirection(e.target);
            if (e.isStart) {
                setCurOperateType(
                    operateType === BaseLayerViewType.RESIZE
                        ? direction || BaseLayerViewType.MOVE
                        : BaseLayerViewType.MOVE,
                );
            }
            if (operateType === BaseLayerViewType.RESIZE) {
                if (direction === ResizeOperateType.LC || direction === ResizeOperateType.RC) {
                    // 调整大小、方向
                    arrowOperate.transform(e, layer, direction);
                } else {
                    // 调整高度
                    arrowOperate.resize(e, layer, direction);
                }
            }
            if (e.isEnd) {
                setCurOperateType(null);
            }
        },
        MoveListenerLevel.DEFAULT_LEVEL,
        'arrow-operate-default',
    );

    const data = layer.data as IMarkupLayerProps;
    const cursor = useResizeCursor(layer.rotate);

    return (
        <>
            {hasLine ? (
                <div className="l-operate__markup-arrow-center">
                    <div
                        className={classnames('resize', ResizeOperateType.TC, cursor.TC)}
                        data-operate-type={BaseLayerViewType.RESIZE}
                        data-direction={ResizeOperateType.TC}
                        style={{
                            ...getOperateOpacity(curOperateType, ResizeOperateType.TC),
                        }}
                    ></div>
                    <div
                        className={classnames('resize', ResizeOperateType.BC, cursor.BC)}
                        data-operate-type={BaseLayerViewType.RESIZE}
                        data-direction={ResizeOperateType.BC}
                        style={{
                            ...getOperateOpacity(curOperateType, ResizeOperateType.BC),
                        }}
                    ></div>
                </div>
            ) : null}

            {layer.width > 20 ? (
                <div
                    className={classnames('l-operate__markup-arrow-left')}
                    style={{
                        width: data.fill.width * scale,
                        left: (data.hasLine ? 0 : -data.fill.width / 2) * scale,
                    }}
                >
                    {hasLine ? (
                        <>
                            <div
                                className={classnames('resize', ScaleOperateType.LT, cursor.TC)}
                                data-operate-type={BaseLayerViewType.RESIZE}
                                data-direction={ScaleOperateType.LT}
                                style={{
                                    ...getOperateOpacity(curOperateType, ScaleOperateType.LT),
                                    top: data.centerOffset.y * scale,
                                    marginTop: -data.anchor.arrowLeft[0] * scale,
                                }}
                            ></div>
                            <div
                                className={classnames('resize', ScaleOperateType.LB, cursor.BC)}
                                data-operate-type={BaseLayerViewType.RESIZE}
                                data-direction={ScaleOperateType.LB}
                                style={{
                                    ...getOperateOpacity(curOperateType, ScaleOperateType.LB),
                                    top: data.centerOffset.y * scale,
                                    marginTop: -data.anchor.arrowLeft[1] * scale,
                                }}
                            ></div>
                        </>
                    ) : null}
                    <div
                        className={classnames('resize', ResizeOperateType.LC, cursor.LC)}
                        data-operate-type={BaseLayerViewType.RESIZE}
                        data-direction={data.imageTransform.flipH ? ResizeOperateType.RC : ResizeOperateType.LC}
                        style={{
                            ...getOperateOpacity(
                                curOperateType,
                                data.imageTransform.flipH ? ResizeOperateType.RC : ResizeOperateType.LC,
                            ),
                            top: data.centerOffset.y * scale,
                        }}
                    ></div>
                    {hasLine ? (
                        <>
                            <div
                                className={classnames('resize', ScaleOperateType.LT, cursor.TC)}
                                data-operate-type={BaseLayerViewType.RESIZE}
                                data-direction={ScaleOperateType.LT}
                                style={{
                                    ...getOperateOpacity(curOperateType, ScaleOperateType.LT),
                                    top: data.centerOffset.y * scale,
                                    marginTop: -data.anchor.arrowLeft[0] * scale,
                                }}
                            ></div>
                            <div
                                className={classnames('resize', ScaleOperateType.LB, cursor.BC)}
                                data-operate-type={BaseLayerViewType.RESIZE}
                                data-direction={ScaleOperateType.LB}
                                style={{
                                    ...getOperateOpacity(curOperateType, ScaleOperateType.LB),
                                    top: data.centerOffset.y * scale,
                                    marginTop: -data.anchor.arrowLeft[1] * scale,
                                }}
                            ></div>
                        </>
                    ) : null}
                </div>
            ) : null}

            <div
                className={classnames('l-operate__markup-arrow-right')}
                style={{
                    width: data.fill.width * scale,
                    right: (data.hasLine ? 0 : -data.fill.width / 2) * scale,
                }}
            >
                {hasLine && layer.width > 20 ? (
                    <>
                        <div
                            className={classnames('resize', ScaleOperateType.RT, cursor.TC)}
                            data-operate-type={BaseLayerViewType.RESIZE}
                            data-direction={ScaleOperateType.RT}
                            style={{
                                ...getOperateOpacity(curOperateType, ScaleOperateType.RT),
                                top: data.centerOffset.y * scale,
                                marginTop: -data.anchor.arrowRight[0] * scale,
                            }}
                        ></div>
                        <div
                            className={classnames('resize', ScaleOperateType.RB, cursor.BC)}
                            data-operate-type={BaseLayerViewType.RESIZE}
                            data-direction={ScaleOperateType.RB}
                            style={{
                                ...getOperateOpacity(curOperateType, ScaleOperateType.RB),
                                top: data.centerOffset.y * scale,
                                marginTop: -data.anchor.arrowRight[1] * scale,
                            }}
                        ></div>
                    </>
                ) : null}
                <div
                    className={classnames('resize', ResizeOperateType.RC, cursor.RC)}
                    data-operate-type={BaseLayerViewType.RESIZE}
                    data-direction={data.imageTransform.flipH ? ResizeOperateType.LC : ResizeOperateType.RC}
                    style={{
                        ...getOperateOpacity(
                            curOperateType,
                            data.imageTransform.flipH ? ResizeOperateType.LC : ResizeOperateType.RC,
                        ),
                        top: data.centerOffset.y * scale,
                    }}
                ></div>
                {hasLine && layer.width > 20 ? (
                    <>
                        <div
                            className={classnames('resize', ScaleOperateType.RT, cursor.TC)}
                            data-operate-type={BaseLayerViewType.RESIZE}
                            data-direction={ScaleOperateType.RT}
                            style={{
                                ...getOperateOpacity(curOperateType, ScaleOperateType.RT),
                                top: data.centerOffset.y * scale,
                                marginTop: -data.anchor.arrowRight[0] * scale,
                            }}
                        ></div>
                        <div
                            className={classnames('resize', ScaleOperateType.RB, cursor.BC)}
                            data-operate-type={BaseLayerViewType.RESIZE}
                            data-direction={ScaleOperateType.RB}
                            style={{
                                ...getOperateOpacity(curOperateType, ScaleOperateType.RB),
                                top: data.centerOffset.y * scale,
                                marginTop: -data.anchor.arrowRight[1] * scale,
                            }}
                        ></div>
                    </>
                ) : null}
            </div>
        </>
    );
});

const SegmentOperate: React.FC<{ layer: ILayerProps }> = React.memo(({ layer }) => {
    const [curOperateType, setCurOperateType] = useState<ResizeOperateType | null>(null);
    const scale = useScale();
    const segmentOperate = useSegmentOperate();

    useMoveEventListener(
        (e: ITouchKitEvent) => {
            if (!layer || layer.layerType !== LayerType.MARKUP_LAYER) return;
            const operateType = getOperateType(e.target);
            const direction = e.target.getAttribute('data-direction') as ResizeOperateType;
            if (e.isStart) {
                setCurOperateType(direction || 'move');
            }
            if (operateType === BaseLayerViewType.RESIZE) {
                segmentOperate.point(e, layer, direction);
            }
            if (e.isEnd) {
                setCurOperateType(null);
            }
        },
        MoveListenerLevel.DEFAULT_LEVEL,
        'segment-operate-default',
    );

    if (!layer || layer.layerType !== LayerType.MARKUP_LAYER) return null;

    const { centerOffset, anchor, fill } = layer.data as IMarkupLayerProps;
    const { rotate } = layer;
    const leftRotate = -getPointsBetweenAngle(anchor.points![0], {
        x: 1,
        y: 0,
    });
    const cursorLeft = useResizeCursor(leftRotate + rotate);

    const rightRotate = -getPointsBetweenAngle(anchor.points![1], {
        x: 1,
        y: 0,
    });
    const cursorRight = useResizeCursor(rightRotate + rotate);
    const cursor = useResizeCursor(rotate);

    return (
        <div
            className="l-operate__markup-segment"
            style={{
                left: centerOffset.x * scale,
                top: centerOffset.y * scale,
            }}
        >
            <div
                className={classnames('resize', cursor.TC)}
                data-operate-type={BaseLayerViewType.RESIZE}
                data-direction={ResizeOperateType.TC}
                style={{
                    ...getOperateOpacity(curOperateType, ResizeOperateType.TC),
                    left: '50%',
                    top: '50%',
                    right: 'auto',
                    transform: 'translate(-50%,-50%)',
                    cursor: 'grabbing',
                }}
            ></div>

            <div
                className="l-operate__markup-segment-left"
                style={{
                    width:
                        (+Math.sqrt(Math.pow(anchor.points![0].x, 2) + Math.pow(anchor.points![0].y, 2)) +
                            fill.width / 2) *
                        scale,
                    transform: `translateY(-50%) rotate(${leftRotate}deg)`,
                    transformOrigin: 'left',
                }}
            >
                <div
                    className={classnames('resize', cursorLeft.LC)}
                    data-operate-type={BaseLayerViewType.RESIZE}
                    data-direction={ResizeOperateType.LC}
                    style={{
                        ...getOperateOpacity(curOperateType, ResizeOperateType.LC),
                        top: '50%',
                        transform: `translate(${curOperateType === ResizeOperateType.LC ? 20 : 10}px,-50%)`,
                        // transform: 'translate(0,-5px)',
                    }}
                ></div>
            </div>
            <div
                className="l-operate__markup-segment-right"
                style={{
                    width:
                        (+Math.sqrt(Math.pow(anchor.points![1].x, 2) + Math.pow(anchor.points![1].y, 2)) +
                            fill.width / 2) *
                        scale,
                    transform: `translateY(-50%) rotate(${-getPointsBetweenAngle(anchor.points![1], {
                        x: 1,
                        y: 0,
                    })}deg)`,
                    transformOrigin: 'left',
                }}
            >
                <div
                    className={classnames('resize', cursorRight.RC)}
                    data-operate-type={BaseLayerViewType.RESIZE}
                    data-direction={ResizeOperateType.RC}
                    style={{
                        ...getOperateOpacity(curOperateType, ResizeOperateType.RC),
                        transform: `translate(${curOperateType === ResizeOperateType.RC ? 20 : 10}px,-50%)`,
                        // transform: 'translate(0,-5px)',
                    }}
                ></div>
            </div>
        </div>
    );
});

const ActiveOperate: React.FC<{
    layer: ILayerProps;
}> = React.memo(({ layer }) => {
    const data = layer.data as IMarkupLayerProps;
    const flipH = data.imageTransform.flipH;
    const flipV = data.imageTransform.flipV;

    let rotate = '';
    if (flipH) {
        rotate = 'rotateY(180deg) ';
    }
    if (flipV) {
        const ry180 = 'rotateX(180deg)';
        rotate = rotate ? rotate + ry180 : ry180;
    }
    const imageInnerStyle: React.CSSProperties = {
        transform: `translate(0px, 0px) ${rotate} scale(1)`,
        transformOrigin: 'center center',
    };
    return (
        <div
            style={{
                ...imageInnerStyle,
                pointerEvents: 'none',
                left: 0,
                right: 0,
                bottom: 0,
                top: 0,
                position: 'absolute',
            }}
        >
            {data.markupType === MarkupType.Arrow ? <ArrowOperate layer={layer} /> : null}
            {data.markupType === MarkupType.Segment ? <SegmentOperate layer={layer} /> : null}
        </div>
    );
});

export default ActiveOperate;
